import { useState, useEffect } from "react";

export interface Country {
  code: string;
  name: string;
  flag: string;
}

export interface CountryDetectionResult {
  countries: Country[];
  userCountry: string | null;
  loading: boolean;
  error: string | null;
}

export const useCountries = (): CountryDetectionResult => {
  const [countries, setCountries] = useState<Country[]>([]);
  const [userCountry, setUserCountry] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchCountriesAndLocation = async () => {
      try {
        setLoading(true);
        setError(null);

        // Fetch all countries
        const countriesResponse = await fetch(
          "https://restcountries.com/v3.1/all?fields=name,cca2,flag",
        );

        if (!countriesResponse.ok) {
          throw new Error("Failed to fetch countries");
        }

        const countriesData = await countriesResponse.json();

        // Transform and sort countries
        const formattedCountries: Country[] = countriesData
          .map((country: any) => ({
            code: country.cca2,
            name: country.name.common,
            flag: country.flag,
          }))
          .sort((a: Country, b: Country) => a.name.localeCompare(b.name));

        setCountries(formattedCountries);

        // Detect user's country using IP geolocation
        try {
          const locationResponse = await fetch("https://ipapi.co/json/");

          if (locationResponse.ok) {
            const locationData = await locationResponse.json();
            const detectedCountryCode = locationData.country_code;

            // Verify the detected country exists in our countries list
            const countryExists = formattedCountries.some(
              (country) => country.code === detectedCountryCode,
            );

            if (countryExists) {
              setUserCountry(detectedCountryCode);
            } else {
              // Fallback to US if detected country is not in our list
              setUserCountry("US");
            }
          } else {
            // Fallback to US if geolocation fails
            setUserCountry("US");
          }
        } catch (locationError) {
          console.warn(
            "Failed to detect user location, defaulting to US:",
            locationError,
          );
          setUserCountry("US");
        }
      } catch (err) {
        console.error("Error fetching countries:", err);
        setError("Failed to load countries");

        // Fallback to a basic list of countries
        const fallbackCountries: Country[] = [
          { code: "US", name: "United States", flag: "🇺🇸" },
          { code: "CA", name: "Canada", flag: "🇨🇦" },
          { code: "GB", name: "United Kingdom", flag: "🇬🇧" },
          { code: "DE", name: "Germany", flag: "🇩🇪" },
          { code: "FR", name: "France", flag: "🇫🇷" },
          { code: "AU", name: "Australia", flag: "🇦🇺" },
          { code: "JP", name: "Japan", flag: "🇯🇵" },
          { code: "IN", name: "India", flag: "🇮🇳" },
          { code: "IL", name: "Israel", flag: "🇮🇱" },
        ];

        setCountries(fallbackCountries);
        setUserCountry("US");
      } finally {
        setLoading(false);
      }
    };

    fetchCountriesAndLocation();
  }, []);

  return { countries, userCountry, loading, error };
};
